import { WebCrud } from '../../../containers/crud/webCrud';
import { Crud } from '../../../containers/crud/crud';
import { WebsitesForm } from './form';
import { EditWebsiteButton } from './editWebsiteButton';
import { useCrudRecord } from '../../../containers/crud/recordContext';
import { useCrudData } from '../../../containers/crud/crudContext';
import { RedeployWebsite } from './redeployWebsite';
import { createDeploymentLink } from './helpers';
import { WebsiteQueriesButton } from './websiteQueriesButton';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { WebsitePaymentButton } from './websitePaymentButton';
import { ExtendTrialLicence } from './extendTrialLicence';
import { ResetWebsiteConfigurations } from './resetWebsiteConfigurations';
import { ExportWebsite } from './exportWebsite';
import { DuplicateWebsite } from './duplicateWebsite';
import { useSecureApi } from '../../../hooks/api';
import { useEffect, useRef } from 'react';
import { url } from '../../../constants/api';
import { LinkDomainForm } from './link-domain/linkDomainForm';
import { AddWebsiteConfig } from './addWebsiteConfig';
import { MigrateThemeConfig } from './migrateThemeConfig';
import { RedeployAllWebsite } from './redeployAllWebsites';

export default function Websites() {
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'ADMIN';
  const refreshTimeout = useRef(null);

  const { callApi: getUsers, data: users } = useSecureApi();

  useEffect(() => {
    if (isAdmin) {
      getUsers(url('users'), {
        params: { all: true },
      });
    }
  }, []);

  return (
    <WebCrud api="websites" FormComponent={WebsitesForm}>
      <Crud.Header title="Websites">
        {isAdmin && <Crud.Header.Actions.Add />}
        {isAdmin && <Crud.Header.Actions.Refresh />}
        {isAdmin && <MigrateThemeConfig />}
        {isAdmin && <RedeployAllWebsite />}
      </Crud.Header>
      <Crud.Table
        actions={[
          () => {
            const { deploymentStatus, active } = useCrudRecord();
            if (deploymentStatus === 'Completed') {
              return [
                ...(active || isAdmin
                  ? [<EditWebsiteButton key={'EditWebsiteButton'} />]
                  : []),
                <WebsitePaymentButton key={'WebsitePaymentButton'} />,
                ...(active || isAdmin
                  ? [<LinkDomainForm key={'LinkDomainForm'} />]
                  : []),
                ...(isAdmin
                  ? [
                      <ExtendTrialLicence key={'ExtendTrialLicence'} />,
                      <Crud.Table.RowActions.Edit key={'edit'} />,
                      <Crud.Table.RowActions.Delete key={'delete'} />,
                      <ResetWebsiteConfigurations
                        key={'ResetWebsiteConfigurations'}
                      />,
                      <AddWebsiteConfig key={'AddWebsiteConfig'} />,
                      <DuplicateWebsite
                        key={'CopyWebsite'}
                        isAdmin={isAdmin}
                        users={users}
                      />,
                    ]
                  : []),

                ...(active || isAdmin
                  ? [<WebsiteQueriesButton key={'WebsiteQueriesButton'} />]
                  : []),

                isAdmin && <RedeployWebsite key={'RedeployWebsite'} />,
                isAdmin && <ExportWebsite key={'ExportWebsite'} />,
              ];
            } else if (deploymentStatus === 'Failed') {
              return <RedeployWebsite key={'RedeployWebsite'} />;
            }
          },
        ]}
        columns={[
          ...(isAdmin
            ? [
                {
                  title: 'Key',
                  dataIndex: 'key',
                  width: 50,
                },
                {
                  title: 'Slug',
                  dataIndex: 'slug',
                  width: 100,
                },
              ]
            : []),

          {
            title: 'Name',
            dataIndex: 'name',
            width: 100,
          },
          {
            title: 'Url',
            dataIndex: 'deploymentUrl',
            width: 150,
            render: (url, _) => {
              const deploymentUrl = createDeploymentLink(url, _.slug);
              return (
                <a href={deploymentUrl} target="_blank" rel="noreferrer">
                  {deploymentUrl}
                </a>
              );
            },
          },
          {
            title: 'Deployment Status',
            dataIndex: 'deploymentStatus',
            width: 100,
            render: (deploymentStatus) => {
              const { refresh } = useCrudData();
              if (
                (deploymentStatus === 'In Progress' ||
                  deploymentStatus === 'Pending') &&
                !refreshTimeout.current
              ) {
                refreshTimeout.current = setTimeout(() => {
                  refresh();
                  refreshTimeout.current = null;
                }, 10000);
              }
              return deploymentStatus;
            },
          },
          {
            title: 'Plan Status',
            dataIndex: 'activePlan',
            width: 100,
          },
        ]}
      >
        <Crud.Table.Header>
          <Crud.Search
            fields={[
              {
                type: 'text',
                name: 'name',
                inputProps: {
                  placeholder: 'Website Name',
                },
              },
            ]}
          />
          {isAdmin && <Crud.Table.BulkRowActions.Common />}
        </Crud.Table.Header>
      </Crud.Table>
    </WebCrud>
  );
}
